/**
 * Authentifizierungs-Kontext: hält den eingeloggten Benutzer, stellt
 * login()/logout() sowie eine hasPermission()-Hilfsfunktion für die
 * clientseitige Sichtbarkeitssteuerung bereit (die eigentliche
 * Rechteprüfung erfolgt serverseitig bei jedem API-Aufruf).
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { api, clearToken, getToken, registerUnauthorizedHandler, setToken } from "../api/client";
import type { AuthUser } from "../types";

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  hasPermission: (permission: string) => boolean;
  /** Lädt den aktuellen Benutzer neu (z.B. nach erzwungener Passwortänderung, um mustChangePassword zu aktualisieren). */
  refreshUser: () => Promise<void>;
  /** Persönliche Anzeige-Präferenz "Reiter ausblenden" (siehe Migration 0046, Settings.tsx). */
  isNavItemHidden: (to: string) => boolean;
  setHiddenNavItems: (items: string[]) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  const logout = useCallback(() => {
    clearToken();
    setUser(null);
  }, []);

  useEffect(() => {
    registerUnauthorizedHandler(logout);
  }, [logout]);

  useEffect(() => {
    const token = getToken();
    if (!token) {
      setLoading(false);
      return;
    }
    api
      .get<AuthUser>("/api/auth/me")
      .then(setUser)
      .catch(() => clearToken())
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback(async (username: string, password: string) => {
    const result = await api.post<{ token: string; user: AuthUser }>("/api/auth/login", { username, password });
    setToken(result.token);
    setUser(result.user);
  }, []);

  // Systemadministrator: uneingeschränkter Zugriff auf alle Module, auch
  // solche, deren Berechtigung ihm nicht explizit über role_permissions/
  // user_permissions zugewiesen ist (die Rolle "Systemadministrator" besitzt
  // absichtlich keine Einträge dort - siehe backend middleware/permissions.ts).
  const hasPermission = useCallback(
    (permission: string) => !!user?.isSuperAdmin || !!user?.permissions.includes(permission),
    [user]
  );

  const refreshUser = useCallback(async () => {
    const fresh = await api.get<AuthUser>("/api/auth/me");
    setUser(fresh);
  }, []);

  // Rechte/Rolle im Hintergrund aktuell halten: /api/auth/me wird sonst nur
  // beim Anmelden und beim Neuladen der Seite gelesen - ändert ein Admin
  // Rechte oder Rolle (oder kommt ein neuer Reiter hinzu), sähe ein bereits
  // offener Browser den alten Stand bis zum nächsten Neuladen. Daher alle 2
  // Minuten (nur bei sichtbarem Tab) sowie sobald der Tab wieder in den
  // Vordergrund kommt (mind. 20s Abstand). Der Benutzer wird nur dann neu
  // gesetzt, wenn sich wirklich etwas geändert hat (kein unnötiges
  // Neurendern). Fehler werden still ignoriert; wurde das Konto
  // zwischenzeitlich deaktiviert, meldet der bestehende 401-Handler ab.
  const userId = user?.id;
  useEffect(() => {
    if (!userId) return;
    const signature = (u: AuthUser) =>
      JSON.stringify([u.username, u.fullName, u.roleName, u.isSuperAdmin, u.mustChangePassword, [...u.permissions].sort(), u.hiddenNavItems]);
    let lastCheck = Date.now();
    let cancelled = false;

    async function check() {
      if (document.visibilityState !== "visible" || Date.now() - lastCheck < 20_000) return;
      lastCheck = Date.now();
      try {
        const fresh = await api.get<AuthUser>("/api/auth/me");
        if (cancelled) return;
        setUser((current) => (current && signature(current) === signature(fresh) ? current : fresh));
      } catch {
        // z.B. kurzzeitig offline - beim nächsten Durchlauf erneut versuchen
      }
    }

    const interval = setInterval(check, 120_000);
    const onVisible = () => { void check(); };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [userId]);

  // Persönliches Giftgrün-Farbschema AUSSCHLIESSLICH für den Login
  // "fratellis" (siehe Anforderung) - setzt/entfernt data-theme auf
  // <html>, die eigentlichen Farbwerte stehen in styles/index.css
  // ([data-theme="skull-devils"], CSS-Variablen --accent/--accent-hover/
  // --accent-muted). Läuft bei jeder Änderung von "user" (Login, Logout,
  // Seiten-Reload nach /api/auth/me), bewusst zentral hier statt in
  // einzelnen Seiten/Komponenten.
  useEffect(() => {
    if (user?.username === "fratellis") {
      document.documentElement.setAttribute("data-theme", "skull-devils");
    } else {
      document.documentElement.removeAttribute("data-theme");
    }
  }, [user]);

  const isNavItemHidden = useCallback((to: string) => !!user?.hiddenNavItems.includes(to), [user]);

  const setHiddenNavItems = useCallback(async (items: string[]) => {
    const result = await api.put<{ hiddenNavItems: string[] }>("/api/auth/nav-preferences", { hiddenItems: items });
    setUser((u) => (u ? { ...u, hiddenNavItems: result.hiddenNavItems } : u));
  }, []);

  const value = useMemo(
    () => ({ user, loading, login, logout, hasPermission, refreshUser, isNavItemHidden, setHiddenNavItems }),
    [user, loading, login, logout, hasPermission, refreshUser, isNavItemHidden, setHiddenNavItems]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth muss innerhalb von <AuthProvider> verwendet werden.");
  return ctx;
}
